import { useEffect, useRef, useState } from "react";
import { Upload, FileText, Trash2, Loader2, BookOpen, Plus, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { listStyleSamples, uploadStyleSample, createStyleSample, deleteStyleSample, listResolutionTypes } from "@/lib/api";

export default function StyleLibrary() {
  const [samples, setSamples] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    category: "",
    resolution_type_key: "",
  });
  const [pasteForm, setPasteForm] = useState({
    title: "",
    category: "",
    resolution_type_key: "",
    body: "",
  });
  const [showPaste, setShowPaste] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t] = await Promise.all([listStyleSamples(), listResolutionTypes()]);
      setSamples(s.data || []);
      setTypes(t.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!form.title) {
      toast.error("Add a title before uploading");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", form.title);
    if (form.category) fd.append("category", form.category);
    if (form.resolution_type_key) fd.append("resolution_type_key", form.resolution_type_key);
    setUploading(true);
    try {
      await uploadStyleSample(fd);
      toast.success("Sample added to style library");
      setForm({ title: "", category: "", resolution_type_key: "" });
      if (fileInputRef.current) fileInputRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handlePasteCreate = async () => {
    if (!pasteForm.title.trim()) { toast.error("Enter a title"); return; }
    if (pasteForm.body.trim().length < 40) { toast.error("Body too short (min 40 chars)"); return; }
    setCreating(true);
    try {
      await createStyleSample(pasteForm);
      toast.success("Sample added");
      setPasteForm({ title: "", category: "", resolution_type_key: "", body: "" });
      setShowPaste(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this style sample?")) return;
    try {
      await deleteStyleSample(id);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const grouped = types.reduce((acc, t) => {
    (acc[t.category] = acc[t.category] || []).push(t);
    return acc;
  }, {});

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="style-library-page">
      <div className="mb-6">
        <div className="label-eyebrow mb-2">Firm House-Style</div>
        <h1 className="font-heading text-4xl font-light tracking-tight">Style Library</h1>
        <p className="text-zinc-600 max-w-2xl text-sm mt-2 leading-relaxed">
          Upload past resolutions you like. Claude reads the 1–2 most relevant samples (by type/category) at generation time and mirrors their tone, phrasing, and structure — without copying facts.
        </p>
      </div>

      {/* Upload card */}
      <div className="border border-zinc-200 bg-white p-6 mb-8" data-testid="upload-card">
        <div className="flex items-center gap-2 mb-4">
          <Upload size={16} className="text-[#002FA7]" />
          <h3 className="font-heading text-lg font-medium tracking-tight">Add a sample</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Field label="Title *">
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="e.g. Bank a/c opening — HDFC"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="sample-title"
            />
          </Field>
          <Field label="Category">
            <input
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              placeholder="banking / shares / directors …"
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="sample-category"
            />
          </Field>
          <Field label="Resolution Type (optional)">
            <select
              value={form.resolution_type_key}
              onChange={(e) => setForm({ ...form, resolution_type_key: e.target.value })}
              className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
              data-testid="sample-rtype"
            >
              <option value="">— Any —</option>
              {Object.entries(grouped).map(([cat, list]) => (
                <optgroup key={cat} label={cat}>
                  {list.map((t) => (
                    <option key={t.key} value={t.key}>{t.title}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="swiss-btn-primary cursor-pointer" data-testid="upload-file-btn">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={handleUpload}
              disabled={uploading || !form.title}
              className="hidden"
              data-testid="upload-file-input"
            />
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Uploading…" : "Upload .pdf / .docx / .txt"}
          </label>
          <button onClick={() => setShowPaste(!showPaste)} className="swiss-btn-ghost" data-testid="toggle-paste">
            <Plus size={14} /> {showPaste ? "Hide paste form" : "Paste text instead"}
          </button>
          {!form.title && (
            <span className="text-xs text-zinc-500 flex items-center gap-1">
              <AlertCircle size={12} /> Add a title to enable upload
            </span>
          )}
        </div>

        {showPaste && (
          <div className="mt-5 pt-5 border-t border-zinc-200 space-y-3" data-testid="paste-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Title *">
                <input
                  value={pasteForm.title}
                  onChange={(e) => setPasteForm({ ...pasteForm, title: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="paste-title"
                />
              </Field>
              <Field label="Category">
                <input
                  value={pasteForm.category}
                  onChange={(e) => setPasteForm({ ...pasteForm, category: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  data-testid="paste-category"
                />
              </Field>
              <Field label="Resolution Type">
                <select
                  value={pasteForm.resolution_type_key}
                  onChange={(e) => setPasteForm({ ...pasteForm, resolution_type_key: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] bg-white"
                  data-testid="paste-rtype"
                >
                  <option value="">— Any —</option>
                  {Object.entries(grouped).map(([cat, list]) => (
                    <optgroup key={cat} label={cat}>
                      {list.map((t) => (
                        <option key={t.key} value={t.key}>{t.title}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </Field>
            </div>
            <textarea
              value={pasteForm.body}
              onChange={(e) => setPasteForm({ ...pasteForm, body: e.target.value })}
              placeholder="Paste your sample resolution text here (min 40 characters)…"
              rows={8}
              className="w-full border border-zinc-300 px-3 py-2 text-sm font-mono-plex leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              data-testid="paste-body"
            />
            <button onClick={handlePasteCreate} disabled={creating} className="swiss-btn-primary" data-testid="paste-save">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {creating ? "Saving…" : "Save Sample"}
            </button>
          </div>
        )}
      </div>

      {/* Samples list */}
      <div className="border border-zinc-200 bg-white" data-testid="samples-list-wrapper">
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BookOpen size={16} className="text-[#002FA7]" />
            <h3 className="font-heading text-lg font-medium tracking-tight">
              Samples on file
            </h3>
            <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">{samples.length} total</span>
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-zinc-500"><Loader2 size={14} className="inline animate-spin" /></div>
        ) : samples.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500" data-testid="samples-empty">
            No samples yet. Upload your firm's past resolutions to teach the AI your house style.
          </div>
        ) : (
          <ul className="divide-y divide-zinc-100" data-testid="samples-list">
            {samples.map((s) => (
              <li key={s.id} className="p-5 hover:bg-zinc-50 transition-colors" data-testid={`sample-${s.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <FileText size={14} className="text-[#002FA7] flex-shrink-0" />
                      <span className="font-medium text-sm">{s.title}</span>
                      {s.category && (
                        <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-zinc-300 text-zinc-700">{s.category}</span>
                      )}
                      {s.resolution_type_key && (
                        <span className="text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 bg-[#002FA7]/10 text-[#002FA7] font-mono-plex">{s.resolution_type_key}</span>
                      )}
                    </div>
                    <p className="text-xs text-zinc-600 line-clamp-2 leading-relaxed">{s.body.slice(0, 280)}{s.body.length > 280 ? "…" : ""}</p>
                    <div className="text-[10px] uppercase tracking-[0.12em] text-zinc-400 mt-2 font-mono-plex">
                      {s.char_count.toLocaleString()} chars · added {new Date(s.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="text-zinc-400 hover:text-red-600 transition-colors flex-shrink-0"
                    title="Delete"
                    data-testid={`sample-delete-${s.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-1.5">{label}</span>
      {children}
    </label>
  );
}
